/**
 * Os documentos fora do exame pela custódia (E136.F3, GUS-152, ADR-0092 §2.3): cada um depende de
 * um arquivo que não confere com o registro do recebimento — o próprio, ou o pacote, o arquivo de
 * origem ou o material do banco —, ou que foi tirado do exame na resolução da ocorrência. Os demais
 * documentos seguem; a ocorrência e a resolução ficam na custódia do caso.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData, ObjetoBloqueadoData } from '@/lib/exame_eletronico';
import { tituloDaPeca } from '@/lib/exame_eletronico_peca';

const t = ptBR.exameEletronico.paradasPelaCustodia;

function motivo(parada: ObjetoBloqueadoData, nome: string): string {
  const base = (parada.objeto_id === parada.source_document_id ? t.proprio : t.dependencia).replace(
    '{documento}',
    nome,
  );
  if (parada.acao === 'retirado_do_exame') return `${base} ${t.retirado_do_exame}`;
  if (parada.acao === 'reenviado_pelo_banco') return `${base} ${t.reenviado_pelo_banco}`;
  return base;
}

export interface PecasParadasPelaCustodiaProps {
  paradas: readonly ObjetoBloqueadoData[];
  pecas: readonly ExameEletronicoCcbData[];
}

export function PecasParadasPelaCustodia({
  paradas,
  pecas,
}: PecasParadasPelaCustodiaProps): JSX.Element | null {
  if (paradas.length === 0) return null;
  const nomes = new Map(pecas.map((p, indice) => [p.source_document_id, tituloDaPeca(p, indice)]));
  return (
    <div
      role="status"
      data-testid="pecas-paradas-pela-custodia"
      className="border border-warning/30 bg-warning-50 px-sc-4 py-sc-3 text-body-sm text-warning-700"
    >
      <p className="font-semibold">{t.titulo}</p>
      <ul className="mt-1 list-disc pl-sc-4">
        {paradas.map((parada) => (
          <li
            key={parada.source_document_id}
            data-testid={`peca-parada-${parada.source_document_id}`}
          >
            {motivo(parada, nomes.get(parada.source_document_id) ?? parada.source_document_id)}
          </li>
        ))}
      </ul>
      <p className="mt-1">{t.explica}</p>
    </div>
  );
}
